// ─── Enums / Unions ───────────────────────────────────────────────────────────

export type TournamentStatus = 'registration' | 'active' | 'complete';
export type TournamentFormat = 'shootout' | 'snooker900' | 'standard';
export type MatchStatus = 'pending' | 'active' | 'complete';
export type BallColour =
  | 'red'
  | 'yellow'
  | 'green'
  | 'brown'
  | 'blue'
  | 'pink'
  | 'black'
  | 'white'; // white = cue ball (foul)

export type FoulType =
  | 'foul_miss'
  | 'foul_cue_ball_potted'
  | 'foul_wrong_ball'
  | 'foul_no_contact'
  | 'foul_ball_off_table';

// ─── Shootout Comps specific ────────────────────────────────────────────────

export const BALL_VALUES: Record<BallColour, number> = {
  red: 1,
  yellow: 2,
  green: 3,
  brown: 4,
  blue: 5,
  pink: 6,
  black: 7,
  white: 0,
};

export const FOUL_MINIMUM = 4; // minimum foul penalty

export const SHOOTOUT_MATCH_DURATION_SECONDS = 600;    // 10 minutes total
export const SHOOTOUT_HALF_TIME_SECONDS = 300;           // 5 minutes — shot clock changes here
export const SHOOTOUT_SHOT_TIME_SECONDS_FIRST_HALF = 15; // first 5 minutes: 15s per shot
export const SHOOTOUT_SHOT_TIME_SECONDS_SECOND_HALF = 10; // last 5 minutes: 10s per shot
/** @deprecated use SHOOTOUT_SHOT_TIME_SECONDS_FIRST_HALF */
export const SHOOTOUT_SHOT_TIME_SECONDS_NORMAL = 15;
/** @deprecated use SHOOTOUT_SHOT_TIME_SECONDS_SECOND_HALF */
export const SHOOTOUT_SHOT_TIME_SECONDS_FINAL_MINUTE = 10;

// ─── Player ───────────────────────────────────────────────────────────────────

export interface Player {
  _id: string;
  ownerId?: string;            // user who created this player record
  name: string;
  seed: number;
  isBye?: boolean;
  profilePictureUrl?: string; // base64 data URL e.g. data:image/jpeg;base64,...
  handicap?: number;           // snooker handicap (higher = weaker player)
  /** Optional link to a real login account (UserModel._id) — set by the tournament owner/admin,
   *  or self-claimed by the account holder. Unlocks that person seeing personal stats. */
  linkedUserId?: string;
}

// ─── Shot ─────────────────────────────────────────────────────────────────────

export interface Shot {
  playerId: string;
  ballPotted: BallColour | null; // null = no pot / safety
  points: number;                // positive for pot, negative for foul
  isFoul: boolean;
  foulType?: FoulType;
  foulPenalty?: number;
  timestamp: string;             // ISO
  shotDurationSeconds: number;
  // True only when this pot nominated a ball to stand in for the real ball on (a genuine
  // free-ball substitution) — scores the real ball's value instead of the potted ball's own,
  // and doesn't advance the red/colour sequence. Never true for a miss or foul.
  isFreeBall?: boolean;
  // True whenever a free ball was on offer for this shot, regardless of what was played —
  // lets undo correctly restore the offer if this shot (a miss, foul, or non-substitution pot
  // taken while the offer was live) gets undone.
  freeBallOffered?: boolean;
}

// ─── Frame / Live Match State ─────────────────────────────────────────────────

export interface LiveMatchState {
  matchId: string;
  player1Id: string;
  player2Id: string;
  score1: number;
  score2: number;
  currentPlayerId: string;       // who is currently at the table
  shots: Shot[];
  matchStartedAt: string | null; // ISO — null = not yet started
  matchEndedAt: string | null;   // ISO
  elapsedSeconds: number;        // updated by server tick
  shotStartedAt: string | null;  // ISO — when current shot clock started
  reds_remaining: number;        // starts at 15 for a full rack (shootout may vary)
  phase: 'not_started' | 'in_progress' | 'complete';
  winnerId: string | null;
  status: MatchStatus;
  clockPaused: boolean; // true when shot clock has expired — match clock stops
  breakOffPending: boolean; // true after break-off button pressed but before first shot — match clock runs, no shot clock
  handicapStart1: number; // points given to player1 at match start (0 if no handicap)
  handicapStart2: number; // points given to player2 at match start (0 if no handicap)
  // Timing config — set at match start from tournament format, so clients don't need to re-fetch
  matchDurationSeconds: number;   // total match length (600 shootout / 900 snooker900)
  halfTimeSeconds: number;        // when shot clock changes (300 shootout / 0 = no half-time for 900)
  shotTimeFirstHalf: number;      // shot clock first half (15s shootout / 20s 900)
  shotTimeSecondHalf: number;     // shot clock second half (10s shootout / 20s 900)
  isStandard?: boolean;           // true for 'standard' format tournaments — no match/shot clock
  startingReds?: number;          // reds the rack started with (15 normally, 6 for sixReds tournaments)
  currentFrame?: number;          // 1-indexed — which frame of the match's bestOf this liveState represents
  // True when the CURRENT player has an outstanding free-ball offer for their very next shot —
  // declared by the referee after a foul that leaves them snookered (the app can't detect this
  // itself, since it doesn't track ball positions). Expands legal balls for that one shot only;
  // cleared automatically once the shot (pot, miss, or foul) is recorded.
  freeBallAvailable?: boolean;
}

// ─── Frame ────────────────────────────────────────────────────────────────────

/** Archived result of one completed frame within a best-of-N match. */
export interface FrameResult {
  frameNumber: number;   // 1-indexed
  score1: number;
  score2: number;
  winnerId: string;      // _id of the player who won this frame
  shots: Shot[];         // this frame's own shot history (not the whole match's)
  highBreak1: number;
  highBreak2: number;
  elapsedSeconds: number;
}

// ─── Match ────────────────────────────────────────────────────────────────────

export interface Match {
  _id: string;
  player1: Player;
  player2: Player | null;
  winner: Player | null;
  score1: number | null;
  score2: number | null;
  status: MatchStatus;
  liveState?: LiveMatchState;
  /** Permanent archive of every shot played in the current/final frame — copied from
   *  liveState.shots when that frame ends. For a completed frame that ISN'T the last one,
   *  its shots live in `frames[i].shots` instead — this field always mirrors the latest frame. */
  shotHistory?: Shot[];
  /** Frames required to win the match (first to reach a majority). Defaults to 1 (today's
   *  behaviour — a single frame decides the match) when absent, so existing matches/tournaments
   *  need no migration. */
  bestOf?: number;
  frameWins1?: number;   // frames won by player1 so far
  frameWins2?: number;   // frames won by player2 so far
  /** Archive of every COMPLETED frame (not including the current one in progress). Empty/absent
   *  for a bestOf=1 match, since there's nothing to archive before the match itself completes. */
  frames?: FrameResult[];
  /** League fixtures only — set per-side when a team is short-handed and fields the same player
   *  in more than one match on the night. Excludes that side's own notable break(s)/win from the
   *  season's high-breaks/most-wins leaderboards; the other side's stats and the fixture's own
   *  score are unaffected. player1 = home, player2 = away (see the /lineup route). */
  excludeHomeFromSeasonStats?: boolean;
  excludeAwayFromSeasonStats?: boolean;
}

// ─── Round ────────────────────────────────────────────────────────────────────

export interface Round {
  round: number;
  name: string;
  matches: Match[];
}

// ─── Tournament ───────────────────────────────────────────────────────────────

export interface PrizePlacement {
  place: number;   // 1 = 1st, 2 = 2nd, etc.
  amount: number;  // prize money for this place
}

export interface Tournament {
  _id: string;
  name: string;
  status: TournamentStatus;
  players: Player[];
  rounds: Round[];
  adminPin: string;
  ownerId: string;
  /** Other users' _ids granted owner-equivalent day-to-day management access (scoring,
   *  settings, starting, roster changes pre-start, pause/resume clocks) — everything except
   *  deleting the tournament or managing this list itself, which stay owner-only. */
  adminIds?: string[];
  maxPlayers: number;           // bracket size: 2,4,8,16,32,64,128
  scheduledAt: string | null;   // ISO — user-set tournament start date/time
  isHandicap: boolean;          // whether handicap scoring is enabled
  location?: string | null;     // venue / location name
  entryFee?: number | null;     // entry fee amount
  currency?: string;            // ISO 4217 currency code e.g. 'GBP'
  prizes?: PrizePlacement[];    // prize money per finishing place
  highBreakPrize?: number | null; // optional prize for the highest break in the tournament
  format?: TournamentFormat;    // match format: shootout (default) or snooker900
  sixReds?: boolean;            // if true, only 6 reds are used instead of 15
  /** Best-of-frames for each round, index 0 = round 1 (standard/snooker900 only — shootout
   *  matches are always a single frame). Absent or a missing/invalid entry defaults to 1. */
  bestOfByRound?: number[];
  isPublic?: boolean;           // if true, any signed-in user can view the tournament and spectate its matches
  topBreaks?: {                 // top 10 breaks scored in this tournament, sorted desc
    playerId: string;
    playerName: string;
    points: number;
    matchId: string;
    breakIdx: number;           // ordinal within the frame to distinguish multiple breaks
    frameNumber: number;        // 1-indexed frame within the match this break occurred in
  }[];
  /** @deprecated use topBreaks[0] */
  highestBreak?: {
    playerId: string;
    playerName: string;
    points: number;
    matchId: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

// ─── League ───────────────────────────────────────────────────────────────────
// A season-long, team-based competition (League → Division(s) → Team(s) with rosters →
// Fixture(s) between two teams → individual frame-matches between one player per
// side). Structurally separate from Tournament (single-elimination bracket) — see
// libs/models/src/league.model.ts for the full design rationale.

export type LeagueStatus = 'setup' | 'active' | 'complete';
export type LeagueFormat = 'handicap' | 'scratch'; // v1 scope — 'aggregate' may be added later
export type FixtureStatus = 'scheduled' | 'lineup_set' | 'in_progress' | 'complete';
export type LeagueTiebreaker = 'points' | 'frameDifference' | 'framesWon' | 'matchesWon' | 'headToHead' | 'awayWins';

export interface LeaguePlayer {
  _id: string;
  name: string;
  handicap?: number;
  profilePictureUrl?: string;
  /** Optional link to a real login account — self-claimed or admin-linked. Unlocks personal
   *  stats for that person when signed in. */
  linkedUserId?: string | null;
  /** Optional link to an existing standalone (tournament-library) Player entity — an identity
   *  assertion set by an owner/admin, so the same person's tournament and league stats can be
   *  associated. */
  linkedPlayerId?: string | null;
  /** Set instead of linkedUserId when the league has requireClaimApproval:true — an owner/admin
   *  must approve-claim or reject it before the claim takes effect. */
  pendingClaimUserId?: string | null;
  /** Set when an owner/admin links this player to someone else's account (PUT .../link-user) —
   *  doesn't take effect until that account holder confirms via the emailed link (or clears it
   *  by rejecting, if it was a mistake). The confirm/reject token itself is never sent to the
   *  client — only this id, so the owner's UI can show "awaiting confirmation from X". */
  pendingLinkUserId?: string | null;
  /** Server-internal only — the confirm/reject email link's secret token and expiry. Declared
   *  here purely so the Mongoose model (whose subdocument shape is inferred structurally from
   *  this type, unlike UserDocument's separate server-side extension) type-checks; every League
   *  API response strips both fields before they ever reach a client — see
   *  stripLeagueLinkSecrets (apps/api/src/helpers.ts) and LeagueSchema's toJSON transform. Never
   *  read these client-side. */
  pendingLinkToken?: string | null;
  pendingLinkTokenExpiry?: string | null;
}

export interface Team {
  _id: string;
  name: string;
  captainPlayerId?: string | null;
  roster: LeaguePlayer[];
  /** User accounts (UserModel._id) granted the "team admin" role for this team — grantable by
   *  the league owner, a league admin, or a superuser. Purely a marker today; no behavior is
   *  gated on it yet, but it exists as a foundation for future team-scoped features. */
  teamAdminUserIds?: string[];
}

/** One frame (match) within a Fixture — a lightweight snapshot mirroring Tournament's
 *  embedded Match shape, minus bestOf/frameWins since a match is always exactly one frame.
 *  The live-scored version is an ordinary MatchModel document (see match-parent.ts). */
export interface FrameMatch {
  _id: string;
  player1: Player;
  player2: Player | null;
  winner: Player | null;
  score1: number | null;
  score2: number | null;
  status: MatchStatus;
  /** See Match.excludeHomeFromSeasonStats/excludeAwayFromSeasonStats — mirrored here so the
   *  lineup UI can show/edit them without a separate fetch. */
  excludeHomeFromSeasonStats?: boolean;
  excludeAwayFromSeasonStats?: boolean;
}

export interface Fixture {
  _id: string;
  homeTeamId: string;
  awayTeamId: string;
  homeTeamName: string; // denormalized snapshot for display
  awayTeamName: string;
  scheduledAt: string | null; // ISO — always editable
  venue?: string | null;
  status: FixtureStatus;
  frameMatches: FrameMatch[];
  homeFrameWins: number | null; // computed + cached once every match completes
  awayFrameWins: number | null;
  winnerTeamId: string | null;  // null = draw, once complete
  completedAt: string | null;
}

export interface Division {
  _id: string;
  name: string;
  format: LeagueFormat;
  framesPerFixture: number;
  /** Caps the starting-score gap a handicap match can open with — null/unset means uncapped.
   *  Only meaningful when format is 'handicap'. */
  maxHandicapDifference?: number | null;
  // A team's points for one fixture = (frames they won × pointsPerFrameWon) + a win/draw/loss
  // bonus — one additive formula covering pure frame-count leagues, pure match-result leagues,
  // and hybrids. Defaults favour pure frame-count scoring (the most common real-world system).
  pointsPerFrameWon: number;
  pointsPerMatchWin: number;
  pointsPerMatchDraw: number;
  pointsPerMatchLoss: number;
  /** Ordered tie-break criteria the standings sort walks through until ties are broken. */
  tiebreakers: LeagueTiebreaker[];
  teams: Team[];
  fixtures: Fixture[];
  seasonStartedAt: string | null; // set when fixtures are generated; locks format/team-removal
  /** Explicit promotion/relegation ordering — tier 1 is the top division. Only meaningful with
   *  2+ divisions in the league. */
  tier?: number;
  /** Default promotion/relegation counts pre-filled into the next end-season preview — the
   *  actual counts used are recorded per-season on archivedSeasons instead. */
  promotionSpots?: number;
  relegationSpots?: number;
  archivedSeasons?: ArchivedSeason[];
}

/** One past send of a league-wide push notification broadcast — see POST /leagues/:id/notify. */
export interface NotificationLogEntry {
  _id: string;
  sentAt: string;
  sentByUserId: string;
  sentByUsername: string;
  title: string;
  body: string;
  divisionId?: string | null;
  divisionName?: string | null; // null/absent = whole league
  recipients: number;
  sent: number;
}

/** A single recorded "notable break" — a player's break met or exceeded the league's configured
 *  threshold in a completed match. See League.notableBreakThreshold and
 *  GET /leagues/:id/notable-breaks. Stored independently of the season lifecycle (a separate
 *  collection, not embedded in the division), so it survives end-of-season resets/archival. */
export interface NotableBreak {
  _id: string;
  leagueId: string;
  divisionId: string;
  divisionName: string;
  teamId: string;
  teamName: string;
  playerId: string;
  playerName: string;
  linkedUserId?: string | null;
  points: number;
  matchId: string;
  frameNumber: number;
  occurredAt: string;
}

export interface League {
  _id: string;
  name: string;
  status: LeagueStatus;
  ownerId: string;
  adminIds?: string[];
  // Any signed-in user (not just owner/admin) can browse and spectate a public league — mirrors
  // Tournament.isPublic. See publicLeague.routes.ts.
  isPublic?: boolean;
  // Opt-in per league (default false — preserves instant-claim behavior). When true, roster
  // self-claim goes through an owner/admin approve/reject step instead of linking immediately.
  requireClaimApproval?: boolean;
  // A break at or above this many points is "notable" for this league and gets recorded (see
  // NotableBreak) — e.g. 26 for a club league's traditional "26+" mention. null/unset disables
  // recording entirely; applies uniformly across every division regardless of format, since raw
  // break size isn't affected by handicap scoring.
  notableBreakThreshold?: number | null;
  divisions: Division[];
  // Most recent broadcasts first, capped at 50 — see POST /leagues/:id/notify.
  notificationLog?: NotificationLogEntry[];
  createdAt: string;
  updatedAt: string;
}

/** Per-competition played/won/lost/high-break/avg-shot-time breakdown — see
 *  GET /api/auth/me/league-stats in apps/api/src/routes/auth.routes.ts. */
export interface CompetitionStats {
  played: number;
  won: number;
  lost: number;
  highBreak: number;
  avgShotTimeSeconds: number | null;
}

export interface MeLeagueStats {
  leagues: {
    leagueId: string; leagueName: string;
    divisionId: string; divisionName: string;
    teamId: string; teamName: string;
    playerId: string; playerName: string;
    stats: CompetitionStats;
  }[];
  tournaments: {
    tournamentId: string; tournamentName: string;
    playerId: string; playerName: string;
    stats: CompetitionStats;
  }[];
  summary: {
    totalPlayed: number;
    totalWon: number;
    totalLost: number;
    careerHighBreak: number;
  };
}

/** One row of a computed (not stored) standings table — see computeStandings in
 *  apps/api/src/routes/league.routes.ts. */
export interface TeamStanding {
  teamId: string;
  teamName: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  framesFor: number;
  framesAgainst: number;
  frameDifference: number;
  /** Matches won as the away team — used only by the 'awayWins' tiebreaker. */
  awayWon: number;
  points: number;
}

/** One entry in a division's "most match wins this season" leaderboard — see
 *  GET .../divisions/:divId/leaders/wins (and the public equivalent). */
export interface WinLeaderEntry {
  playerId: string;
  playerName: string;
  teamName: string;
  wins: number;
}

/** One past season's frozen state, pushed onto Division.archivedSeasons by
 *  POST .../end-season/commit — see league.routes.ts. */
export interface ArchivedSeason {
  _id: string;
  seasonLabel: string;
  endedAt: string;
  /** The promotion/relegation counts actually used this season (can differ season to season). */
  promotionSpots: number;
  relegationSpots: number;
  teams: Team[];
  fixtures: Fixture[];
  standings: TeamStanding[];
}

// ─── API shapes ───────────────────────────────────────────────────────────────

export interface AddPlayerRequest {
  name: string;
}


export interface MatchResultRequest {
  score1?: number | null;
  score2?: number | null;
  winnerId: string;
}

export interface UpdateNameRequest {
  name: string;
}

export interface VerifyPinRequest {
  pin: string;
}

export interface ErrorResponse {
  error: string;
}

export interface SuccessResponse {
  success: true;
}

// ─── Socket.io event payloads ─────────────────────────────────────────────────

export interface ShotPayload {
  matchId: string;
  ballPotted: BallColour | null;
  isFoul: boolean;
  foulType?: FoulType;
}

export interface StartMatchPayload {
  matchId: string;
}

export interface EndMatchPayload {
  matchId: string;
  winnerId: string;
}

// ─── Auth / Users ─────────────────────────────────────────────────────────────

export type UserRole = 'superuser' | 'user';
export type UserStatus = 'pending_verification' | 'active' | 'suspended' | 'deactivated';
// Day/month/year display order — DMY = 31/12/2026, MDY = 12/31/2026, YMD = 2026-12-31.
export type DateFormat = 'DMY' | 'MDY' | 'YMD';

export interface LinkedOAuthProvider {
  provider: string;
  linkedAt: string;
}

export interface User {
  _id: string;
  username: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  emailVerified: boolean;
  createdAt: string;
  createdBy?: string;
  suspendedReason?: string;
  freeTournamentsForLife?: boolean;
  marketingConsent?: boolean;
  dateFormat?: DateFormat;
  lastOnline?: string | null;
  linkedProviders?: LinkedOAuthProvider[];
  hasPassword?: boolean;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  user: User;
}

export interface JwtPayload {
  sub: string;       // user _id
  username: string;
  role: UserRole;
  type: 'access' | 'refresh';
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface SignupRequest {
  username: string;
  email: string;
  password: string;
}

export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
}
